import { useState, type KeyboardEvent } from 'react';
import { FolderPlus } from 'lucide-react';

/**
 * "New folder" in the picker: a name, and Create, which makes the folder in the place open and opens
 * it (`create` from the picker's walk); a refused name shows Paige's reason on the line.
 */
export function StorageNewFolder({ onCreate }: { onCreate: (name: string) => Promise<void> }) {
  const folder = useNewFolder(onCreate);
  if (folder.name === null)
    return (
      <button type="button" className="quiet-button small" onClick={() => folder.setName('')}>
        <FolderPlus size={12} aria-hidden="true" />
        New folder
      </button>
    );
  return (
    <span className="paige-storage-new-folder">
      <input
        value={folder.name}
        onChange={(event) => folder.setName(event.target.value)}
        onKeyDown={folder.onKey}
        placeholder="Folder name"
        aria-label="New folder name"
        autoFocus
      />
      <button
        type="button"
        className="quiet-button small"
        disabled={folder.busy}
        onClick={() => void folder.create()}
      >
        Create
      </button>
      <button type="button" className="quiet-button small" onClick={() => folder.setName(null)}>
        Cancel
      </button>
      {folder.error && <small className="paige-destination-error">{folder.error}</small>}
    </span>
  );
}

/** The name being typed (null: the line is closed), and Create with its busy and refused states. */
function useNewFolder(onCreate: (name: string) => Promise<void>) {
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    if (!name?.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(name.trim());
      setName(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Paige could not make the folder');
    } finally {
      setBusy(false);
    }
  };
  /** Enter creates; Escape closes the line, not the sheet around it. */
  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void create();
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      setName(null);
    }
  };
  return { name, setName, busy, error, create, onKey };
}
